<script setup lang="ts">
  import { z } from 'zod';
  import { storeToRefs } from 'pinia';
  import { toInteger } from '@borg/utils';
  import { Item, Suggestion, UserProfile } from '@borg/types';
  import { MpNumber, MpSelect, pushToastMessage } from '@borg/ui';
  import { useUserProfileStore } from '@/layers/MyProfile/stores/userProfile.store';
  import { EditModalEvents } from './types';

  defineProps<{
    isOpen: boolean;
    id?: number | string;
  }>();

  const emits = defineEmits<EditModalEvents>();

  const { t } = useI18n();
  const projectConfig = useProjectConfig();
  const userProfileStore = useUserProfileStore();

  const { data: employmentTypes } = await employmentTypesRepository.getMany();

  const { profile } = storeToRefs(userProfileStore);

  const { values, validate, resetStates, setValues } = useForm({
    initialData: () => transformEntity(),
    schema: z.object({
      locations: z.array(z.object({ id: z.string(), name: z.string() })),
      positions: z.array(z.object({ id: z.number(), name: z.string() })),
      employmentTypes: z.array(z.object({ id: z.number(), name: z.string() })),
      salary: z.object({
        from: z.union([z.number(), z.string(), z.null(), z.undefined()]),
        to: z.union([z.number(), z.string(), z.null(), z.undefined()]),
        currency: z.string().min(1),
      }),
    }),
  });

  const inProgress = computed(() => userProfileStore.updateState === 'inProgress');
  const _values = computed(() => {
    const from = getSalaryValue(values.salary.from);
    const to = getSalaryValue(values.salary.to);
    return {
      locations: values.locations.map((l) => ({ placeId: l.id, name: l.name })),
      positions: values.positions,
      employmentTypes: values.employmentTypes,
      salary: !from && !to ? null : { from, to, currency: values.salary.currency },
    };
  });

  function getSalaryValue(value: number | string | null | undefined): number | null {
    let _value = toInteger(value);
    return _value ?? null;
  }

  async function searchLocations(keyword: string) {
    if (keyword.length < 1) {
      return [];
    }
    const result = await locationsService.search(keyword);
    return result.isSuccess ? result.data : [];
  }

  async function searchPositions(query: string) {
    if (query.length < 1) {
      return [];
    }
    const result = await positionsService.search(query);
    return result.isSuccess ? result.data : [];
  }

  function transformEntity(input?: UserProfile['idealJob']) {
    return {
      locations: input?.locations
        ? input.locations.map((l) => ({ id: l.placeId, name: l.name }))
        : [],
      positions: input?.positions ?? [],
      employmentTypes: input?.employmentTypes ?? [],
      salary: input?.salary
        ? {
            from: input.salary.from ?? undefined,
            to: input.salary.to ?? undefined,
            currency: input.salary.currency,
          }
        : {
            from: undefined,
            to: undefined,
            currency: projectConfig.parameters.currency.default,
          },
    };
  }

  function onModalOpened() {
    setValues(transformEntity(profile.value?.idealJob));
  }

  async function onSubmit() {
    const valid = await validate();
    if (valid) {
      await userProfileStore.update({ idealJob: _values.value });
      if (userProfileStore.updateState === 'isSuccess') {
        emits('change');
        onClose();
      } else {
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.SUBMIT_ERROR_TOAST.SUBTITLE'),
        });
      }
    }
  }

  function onClose() {
    resetStates();
    emits('update:isOpen', false);
  }
</script>

<template>
  <SiteActionsModal
    :is-open="isOpen"
    :title="$t('MY_PROFILE_PAGE.IDEAL_JOB.TITLE')"
    :in-progress="inProgress"
    @open="onModalOpened"
    @confirm="onSubmit"
    @cancel="onClose"
  >
    <MyProfileEditModalForm>
      <MpSelect
        v-model="values.locations"
        :options="searchLocations"
        :get-value="(option: Suggestion) => option.id"
        :get-label="(option: Suggestion) => option.name"
        :label="$t('MY_PROFILE_PAGE.IDEAL_JOB.LOCATIONS_LABEL')"
        :close-on-select="false"
        mode="multiple"
        searchable
        wrap-tags
      />
      <MpSelect
        v-model="values.positions"
        :options="searchPositions"
        :get-value="(option: Suggestion) => option.id"
        :get-label="(option: Suggestion) => option.name"
        :label="$t('MY_PROFILE_PAGE.IDEAL_JOB.POSITIONS_LABEL')"
        :close-on-select="false"
        mode="multiple"
        searchable
        wrap-tags
      />
      <MpSelect
        v-model="values.employmentTypes"
        :options="employmentTypes ?? []"
        :get-value="(option: Item) => option.id"
        :get-label="(option: Item) => option.name"
        :label="$t('MY_PROFILE_PAGE.IDEAL_JOB.EMPLOYMENT_TYPES_LABEL')"
        :close-on-select="false"
        mode="multiple"
        searchable
        wrap-tags
      />
      <MpNumber
        v-model="values.salary.from"
        :label="$t('MY_PROFILE_PAGE.IDEAL_JOB.SALARY_LABEL')"
        :placeholder="$t('FROM')"
        min="0"
        :max="values.salary.to"
        class="form__row-third"
        step="100"
      />
      <MpNumber
        v-model="values.salary.to"
        :placeholder="$t('TO')"
        :min="values.salary.from"
        class="form__row-third no-label"
        step="100"
      />
      <MpSelect
        v-model="values.salary.currency"
        :options="projectConfig.parameters.currency.options"
        :unselectable="false"
        class="form__row-third no-label"
      />
    </MyProfileEditModalForm>
  </SiteActionsModal>
</template>

<style scoped lang="scss">
  .no-label {
    align-self: flex-end;
  }
</style>
